import * as timelineIcons from '@icons/timeline';
import { getMetricDisplayName } from '@utils/metrics';
import { PRODUCT_MODE_MAP } from '@utils/constants';

export enum SESSION_TYPES {
  NORMAL = 'NORMAL',
  SESSION_OVERVIEW = 'SESSION_OVERVIEW',
}
export const EVENT_ICON_SIZE = 14;

export const GROUP_EVENTS = 'GroupEvents';

export enum TIMELINE_DIMENSION {
  SessionState = 'sessionState',
  SessionEvent = 'sessionEvent',
  SessionOverviewEvent = 'sessionOverviewEvent',
  Asset = 'asset',
  Bitrate = 'bitrate',
  Framerate = 'framerate',
  CDN = 'cdn',
  Error = 'error',
  CustomEvent = 'customEvent',
  BackgroundStart = 'BackgroundStart',
}

export const EXPANDABLE_EVENTS: string[] = [
  TIMELINE_DIMENSION.Bitrate,
  TIMELINE_DIMENSION.Framerate,
  TIMELINE_DIMENSION.Error,
  TIMELINE_DIMENSION.CustomEvent,
];

export enum EVENT_NAMES {
  SessionStart = 'SessionStart',
  SessionEnd = 'End',
  SessionRevival = 'SessionRevival',
  SessionExpired = 'Expired',
  Play = 'Play',
  Seek = 'Seek',
  EBVS = 'EBVS',
  VSF = 'VSF',
  VPF = 'VPF',
  FatalError = 'FatalError',
  NonFatalError = 'NonFatalError',
  More = 'More',
}

export enum STATE_NAMES {
  Playing = 'Playing',
  Buffering = 'Buffering',
  Rebuffering = 'ConnectionInducedRebuffering',
  SeekInducedBuffering = 'SeekInducedBuffering',
  Paused = 'Paused',
  FalsePlaying = 'FalsePlaying',
  LongBuffering = 'LongBuffering',
  LongPaused = 'LongPaused',
  Other = 'Other',
  NotMonitored = 'NotMonitored',
  Unknown = 'Unknown',
  RebufferingConnectionInduced = 'Rebuffering(Connection-Induced)',
}

// write return type for below function
export const EVENT_ICON_MAPPING: {
  [key in EVENT_NAMES]: React.FC<React.SVGProps<SVGSVGElement>>;
} = {
  [EVENT_NAMES.SessionEnd]: timelineIcons.TimelineEnd,
  [EVENT_NAMES.SessionStart]: timelineIcons.TimelinePlay,
  [EVENT_NAMES.SessionRevival]: timelineIcons.TimelineRestart,
  [EVENT_NAMES.EBVS]: timelineIcons.TimelineOtherError,
  [EVENT_NAMES.VSF]: timelineIcons.TimelineOtherError,
  [EVENT_NAMES.VPF]: timelineIcons.TimelineOtherError,
  [EVENT_NAMES.FatalError]: timelineIcons.TimelineFatalError,
  [EVENT_NAMES.NonFatalError]: timelineIcons.TimelineNonFatalError,
  [EVENT_NAMES.Seek]: timelineIcons.TimelineSeek,
  [EVENT_NAMES.Play]: timelineIcons.TimelinePlay,
  [EVENT_NAMES.More]: timelineIcons.TimelineGroupEvents,
  [EVENT_NAMES.SessionExpired]: timelineIcons.TimelineExpired,
};

export const STATE_CHART_ICON_MAPPING = {
  [STATE_NAMES.Playing]: timelineIcons.StatePlaying,
  [STATE_NAMES.Buffering]: timelineIcons.StateBuffering,
  [STATE_NAMES.RebufferingConnectionInduced]: timelineIcons.StateRebuffering,
  [STATE_NAMES.Rebuffering]: timelineIcons.StateRebuffering,
  [STATE_NAMES.SeekInducedBuffering]: timelineIcons.StateSeekBuffering,
  [STATE_NAMES.Paused]: timelineIcons.StatePause,
  [STATE_NAMES.FalsePlaying]: timelineIcons.StateFalsePlaying,
  [STATE_NAMES.LongBuffering]: timelineIcons.StateLongBuffering,
  [STATE_NAMES.LongPaused]: timelineIcons.StateLongPaused,
  [STATE_NAMES.Other]: timelineIcons.StateOther,
  [STATE_NAMES.NotMonitored]: timelineIcons.StateNotMonitored,
  // [STATE_NAMES.Unknown]: timelineIcons.StateUn,
};

export const STATE_ICON_MAPPING = {
  ...STATE_CHART_ICON_MAPPING,
  [STATE_NAMES.Buffering]: timelineIcons.StateBufferingKey,
  [STATE_NAMES.Rebuffering]: timelineIcons.StateRebufferingKey,
  [STATE_NAMES.SeekInducedBuffering]: timelineIcons.StateSeekBufferingKey,
};

export const EVENT_DISPLAY_NAMES: Record<EVENT_NAMES, string> = {
  [EVENT_NAMES.SessionStart]: 'Session Start',
  [EVENT_NAMES.SessionEnd]: 'End',
  [EVENT_NAMES.SessionRevival]: 'Session Restart',
  [EVENT_NAMES.EBVS]: EVENT_NAMES.EBVS,
  [EVENT_NAMES.VSF]: EVENT_NAMES.VSF,
  [EVENT_NAMES.VPF]: EVENT_NAMES.VPF,
  [EVENT_NAMES.FatalError]: 'Fatal Error',
  [EVENT_NAMES.NonFatalError]: 'Non-Fatal Error',
  [EVENT_NAMES.Seek]: 'Seek',
  [EVENT_NAMES.Play]: 'Play',
  [EVENT_NAMES.More]: 'More',
  [EVENT_NAMES.SessionExpired]: 'Expired',
};

export const STATE_DISPLAY_NAMES = {
  [STATE_NAMES.Playing]: STATE_NAMES.Playing,
  [STATE_NAMES.Buffering]: STATE_NAMES.Buffering,
  [STATE_NAMES.Rebuffering]: 'Rebuffering',
  [STATE_NAMES.SeekInducedBuffering]: 'Seek Buffering',
  [STATE_NAMES.Paused]: 'Pause',
  [STATE_NAMES.FalsePlaying]: 'False Playing',
  [STATE_NAMES.LongBuffering]: 'Long Buffering',
  [STATE_NAMES.LongPaused]: 'Long Pause',
  [STATE_NAMES.Other]: STATE_NAMES.Other,
  [STATE_NAMES.NotMonitored]: 'Not Monitored',
  [STATE_NAMES.Unknown]: STATE_NAMES.Unknown,
  [STATE_NAMES.RebufferingConnectionInduced]: 'Connection Induced Rebuffering',
};

export enum SUMMARY_KEYS {
  ClientId = 'ClientId',
  PlayStatus = 'PlayStatus',
  SessionStartTimestampMs = 'SessionStartTimestampMs',
  SessionEndTimestampMs = 'SessionEndTimestampMs',
  LatestActiveTimestampMs = 'LatestActiveTimestampMs',
  StartedEarlierThanInterval = 'StartedEarlierThanInterval',
  OngoingAfterInterval = 'OngoingAfterInterval',
  LifeCdnListAsString = 'LifeCdnListAsString',
  VideoStartupTimeMs = 'VideoStartupTimeMs',
  LifePlayingTimeMs = 'LifePlayingTimeMs',
  LifeRebufferingRatio = 'LifeRebufferingRatio',
  LifeRebufferingTimeMs = 'LifeRebufferingTimeMs',
  LifeConnectionInducedRebufferingRatio = 'LifeConnectionInducedRebufferingRatio',
  LifeConnectionInducedRebufferingTimeMs = 'LifeConnectionInducedRebufferingTimeMs',
  LifeAvgBitrateKbps = 'LifeAvgBitrateKbps',
  LifeAverageAvgBitrateKbps = 'LifeAverageAvgBitrateKbps',
  LifeAvgFramesPerSec = 'LifeAvgFramesPerSec',
  LifePausedTimeMs = 'LifePausedTimeMs',
  LifePausedTimePct = 'LifePausedTimePct',
  EndedStatus = 'EndedStatus',
  LifeLastPlayHeadTimeMs = 'LifeLastPlayHeadTimeMs',
  ContentLengthMs = 'ContentLengthMs',
  JoinedAvgBitrateKbps = 'LifeJoinedAvgBitrateKbps',
  LifeNumBitrateSwitches = 'LifeNumBitrateSwitches',
  ViewerId = 'ViewerId',
  DeviceOS = 'DeviceOS',
  AutonomousSystem = 'AutonomousSystem',
  ContentCategory = 'ContentCategory',
  AppName = 'AppName',
  AppVersion = 'AppVersion',
  DeviceName = 'DeviceName',
  DeviceHardwareType = 'DeviceHardwareType',
  DeviceOSFamily = 'DeviceOSFamily',
  BrowserName = 'BrowserName',
  BrowserVersion = 'BrowserVersion',
  ISP = 'ISP',
  Country = 'Country',
  State = 'State',
  City = 'City',
  LifeExitRelatedCIRTimeMs = 'LifeExitRelatedCIRTimeMs',
  LifeEncodedFrames = 'LifeEncodedFrames',
  LifeRenderedFrames = 'LifeRenderedFrames',
  LifeDropFrameRatio = 'LifeDropFrameRatio',
}

export const getSummaryDisplayName = () => ({
  ...EVENT_DISPLAY_NAMES,
  ...STATE_DISPLAY_NAMES,
  [SUMMARY_KEYS.ClientId]: 'Session ID',
  [SUMMARY_KEYS.PlayStatus]: 'Play Status',
  // No sure it will be use when the timeline release to customers.
  [SUMMARY_KEYS.SessionStartTimestampMs]: 'Session Start Time',
  [SUMMARY_KEYS.SessionEndTimestampMs]: 'Session End Time',
  [SUMMARY_KEYS.LatestActiveTimestampMs]: 'Latest Active Timestamp Ms',
  [SUMMARY_KEYS.StartedEarlierThanInterval]: 'Started Earlier Than Interval',
  [SUMMARY_KEYS.OngoingAfterInterval]: 'Ongoing After Interval',
  [SUMMARY_KEYS.LifeCdnListAsString]: 'Cdn List As String',
  [SUMMARY_KEYS.EndedStatus]: 'Ended Status',
  [SUMMARY_KEYS.ViewerId]: 'Viewer Id',
  [SUMMARY_KEYS.LifeLastPlayHeadTimeMs]: 'Last Playhead Time',
  [SUMMARY_KEYS.ContentLengthMs]: 'Content Length',
  [SUMMARY_KEYS.JoinedAvgBitrateKbps]: 'Starting Bitrate',
  [SUMMARY_KEYS.LifeNumBitrateSwitches]: 'Bitrate Switches',
  [SUMMARY_KEYS.DeviceOS]: 'Device OS',
  [SUMMARY_KEYS.AutonomousSystem]: 'Autonomous System',
  [SUMMARY_KEYS.ContentCategory]: 'Content Category',
  [SUMMARY_KEYS.AppName]: 'App Name',
  [SUMMARY_KEYS.AppVersion]: 'App Version',
  [SUMMARY_KEYS.DeviceName]: 'Device Name',
  [SUMMARY_KEYS.DeviceHardwareType]: 'Device Hardware Type',
  [SUMMARY_KEYS.DeviceOSFamily]: 'Device OS Family',
  [SUMMARY_KEYS.BrowserName]: 'Browser Name',
  [SUMMARY_KEYS.BrowserVersion]: 'Browser Version',
  [SUMMARY_KEYS.LifeExitRelatedCIRTimeMs]: 'CIR Time preceding Exit',
  [SUMMARY_KEYS.LifeEncodedFrames]: 'Encoded Frames',
  [SUMMARY_KEYS.LifeRenderedFrames]: 'Rendered Frames',
  [SUMMARY_KEYS.LifeDropFrameRatio]: 'Dropped Frames Ratio',
  [SUMMARY_KEYS.LifePlayingTimeMs]: 'Playing Time',
  [SUMMARY_KEYS.LifeRebufferingTimeMs]: 'Rebuffering Time',
  [SUMMARY_KEYS.LifeConnectionInducedRebufferingTimeMs]: 'Connection Induced Rebuffering Time',
  [SUMMARY_KEYS.VideoStartupTimeMs]: getMetricDisplayName({
    metricKey: 'VideoStartTime',
    context: PRODUCT_MODE_MAP.EI,
  }),
  [SUMMARY_KEYS.LifeRebufferingRatio]: getMetricDisplayName({
    metricKey: 'RebufferingRatio',
    context: PRODUCT_MODE_MAP.EI,
  }),
  [SUMMARY_KEYS.LifeConnectionInducedRebufferingRatio]: getMetricDisplayName({
    metricKey: 'ConnectionInducedRebufferingRatio',
    context: PRODUCT_MODE_MAP.EI,
  }),
  [SUMMARY_KEYS.LifeAvgBitrateKbps]: getMetricDisplayName({
    metricKey: 'Bitrate',
    context: PRODUCT_MODE_MAP.EI,
  }),
  [SUMMARY_KEYS.LifeAverageAvgBitrateKbps]: getMetricDisplayName({
    metricKey: 'AvgBitrate',
    context: PRODUCT_MODE_MAP.EI,
  }),
  [SUMMARY_KEYS.LifeAvgFramesPerSec]: getMetricDisplayName({
    metricKey: 'Framerate',
    context: PRODUCT_MODE_MAP.EI,
  }),
  [SUMMARY_KEYS.LifePausedTimeMs]: getMetricDisplayName({
    metricKey: 'LifePausedTimeMins',
    context: PRODUCT_MODE_MAP.EI,
  }),
  [SUMMARY_KEYS.LifePausedTimePct]: getMetricDisplayName({
    metricKey: 'LifePausedTimePct',
    context: PRODUCT_MODE_MAP.EI,
  }),
});

export const ALL_DISPLAY_NAMES = {
  ...getSummaryDisplayName(),
  [TIMELINE_DIMENSION.SessionState]: 'Session State',
  [TIMELINE_DIMENSION.SessionEvent]: 'Session Event',
  [TIMELINE_DIMENSION.SessionOverviewEvent]: 'Session Overview Event',
  [TIMELINE_DIMENSION.Asset]: 'Asset',
  [TIMELINE_DIMENSION.Bitrate]: 'Bitrate',
  [TIMELINE_DIMENSION.Framerate]: 'Framerate',
  [TIMELINE_DIMENSION.CDN]: 'CDN',
  [TIMELINE_DIMENSION.Error]: 'Error',
  [TIMELINE_DIMENSION.CustomEvent]: 'Custom Event',
  [TIMELINE_DIMENSION.BackgroundStart]: 'Background Start',
  connectionType: 'Connection Type',
};

export const SUMMARY_LIST = [
  SUMMARY_KEYS.SessionStartTimestampMs,
  SUMMARY_KEYS.SessionEndTimestampMs,
  SUMMARY_KEYS.LifePlayingTimeMs,
  SUMMARY_KEYS.Country,
  SUMMARY_KEYS.State,
  SUMMARY_KEYS.City,
];

export const SUMMARY_SECONDARY_LIST = {
  'Quality Metrics': [
    SUMMARY_KEYS.VideoStartupTimeMs,
    SUMMARY_KEYS.LifeRebufferingRatio,
    SUMMARY_KEYS.LifeRebufferingTimeMs,
    SUMMARY_KEYS.LifeConnectionInducedRebufferingRatio,
    SUMMARY_KEYS.LifeConnectionInducedRebufferingTimeMs,
    SUMMARY_KEYS.LifeAvgBitrateKbps,
    SUMMARY_KEYS.LifeAverageAvgBitrateKbps,
    SUMMARY_KEYS.LifeAvgFramesPerSec,
    SUMMARY_KEYS.LifePausedTimeMs,
    SUMMARY_KEYS.LifePausedTimePct,
    SUMMARY_KEYS.JoinedAvgBitrateKbps,
    SUMMARY_KEYS.LifeNumBitrateSwitches,
    SUMMARY_KEYS.EndedStatus,
    SUMMARY_KEYS.LifeExitRelatedCIRTimeMs,
    SUMMARY_KEYS.LifeEncodedFrames,
    SUMMARY_KEYS.LifeRenderedFrames,
    SUMMARY_KEYS.LifeDropFrameRatio,
  ],
  'Device Metadata': [
    SUMMARY_KEYS.DeviceOS,
    SUMMARY_KEYS.DeviceName,
    SUMMARY_KEYS.DeviceHardwareType,
    SUMMARY_KEYS.DeviceOSFamily,
    SUMMARY_KEYS.BrowserName,
    SUMMARY_KEYS.BrowserVersion,
  ],
  'Player Metadata': [SUMMARY_KEYS.AppName, SUMMARY_KEYS.AppVersion],
  'Content Metadata': [
    SUMMARY_KEYS.ContentCategory,
    SUMMARY_KEYS.LifeLastPlayHeadTimeMs,
    SUMMARY_KEYS.ContentLengthMs,
  ],
  'Network Metadata': [SUMMARY_KEYS.ISP],
};

export const SUMMARY_TIME_FORMAT = {
  [SUMMARY_KEYS.SessionStartTimestampMs]: 'HH:mm:ss MMM DD',
  [SUMMARY_KEYS.SessionEndTimestampMs]: 'HH:mm:ss MMM DD',
};

export const SUMMARY_TIME_RANGE_FORMAT = [
  SUMMARY_KEYS.LifePlayingTimeMs,
  SUMMARY_KEYS.LifeRebufferingTimeMs,
];

export const SUMMARY_TIME_HHMMSS_FORMAT = [
  SUMMARY_KEYS.LifePausedTimeMs,
  SUMMARY_KEYS.LifeLastPlayHeadTimeMs,
  SUMMARY_KEYS.ContentLengthMs,
];

export const UNITS = {
  [TIMELINE_DIMENSION.Bitrate]: 'bps',
  [TIMELINE_DIMENSION.Framerate]: 'fps',
  [SUMMARY_KEYS.VideoStartupTimeMs]: 's',
  [SUMMARY_KEYS.LifePlayingTimeMs]: 's',
  [SUMMARY_KEYS.LifeRebufferingRatio]: '%',
  [SUMMARY_KEYS.LifeConnectionInducedRebufferingRatio]: '%',
  [SUMMARY_KEYS.LifeConnectionInducedRebufferingTimeMs]: 's',
  [SUMMARY_KEYS.LifeAvgBitrateKbps]: 'bps',
  [SUMMARY_KEYS.LifeAverageAvgBitrateKbps]: 'bps',
  [SUMMARY_KEYS.LifeAvgFramesPerSec]: 'fps',
  [SUMMARY_KEYS.LifePausedTimeMs]: 's',
  [SUMMARY_KEYS.LifePausedTimePct]: '%',
  [SUMMARY_KEYS.JoinedAvgBitrateKbps]: 'bps',
  [SUMMARY_KEYS.LifeLastPlayHeadTimeMs]: 's',
  [SUMMARY_KEYS.ContentLengthMs]: 's',
  [SUMMARY_KEYS.LifeExitRelatedCIRTimeMs]: 's',
  [SUMMARY_KEYS.LifeDropFrameRatio]: '%',
};

export const MS_METRIC = [
  SUMMARY_KEYS.VideoStartupTimeMs,
  SUMMARY_KEYS.LifePlayingTimeMs,
  SUMMARY_KEYS.LifeConnectionInducedRebufferingTimeMs,
  SUMMARY_KEYS.LifeExitRelatedCIRTimeMs,
];
